import { load } from 'js-yaml';

const EXTERNAL_SOURCES_KEY = 'EXTERNAL SOURCES';

export interface PodDependency {
  name: string;
  version?: string;
  dependencies?: PodDependency[];
}

export interface ExternalSource {
  /** "../node_modules/react-native/third-party-podspecs/DoubleConversion.podspec" */
  ':podspec'?: string;
  /** "../node_modules/expo-application/ios" */
  ':path'?: string;
}

export interface PodfileLock {
  pods?: PodDependency[];
  /** "1.11.2" */
  cocoapods?: string;
  externalSources?: Record<string, ExternalSource>;
  /** 73e35020f8f5d49ffd32debe3c1bdd501f8029a6 */
  podfileChecksum?: string;
  /** { "DoubleConversion": "cf9b38bf0b2d048436d9a82ad2abe1404f11e7de" } */
  specChecksums?: Record<string, string>;
}

/**
 * Parses a podfile.lock file from from YAML into a JSON object.
 *
 * @param str Podfile.lock file contents in YAML format.
 * @returns
 */
export function loadPodfileLock(str: string): null | Record<string, any> {
  const contents = load(str);
  if (!contents || typeof contents !== 'object') {
    return null;
  }
  return contents;
}

export const parsePodDependency = (
  pod: string | Record<string, string | Record<string, any>>
): PodDependency[] => {
  if (typeof pod === 'string') {
    // js-yaml fails to parse an array with a single item and instead formats it as a string divided by a `-` (hyphen).
    // Here we match if a hyphen comes after a space. We use fake-nested-Podfile to test this hack.
    const singleItemArrayBug = pod.match(/(.*)\s-\s(.*)/);
    if (singleItemArrayBug?.[2]) {
      return parsePodDependency({ [singleItemArrayBug[1]]: singleItemArrayBug[2] });
    }

    return [splitPodNameVersion(pod)];
  }
  return Object.entries(pod).map(([k, v]) => {
    const results = splitPodNameVersion(k);

    if (Array.isArray(v)) {
      return {
        ...results,
        dependencies: v.map(x => parsePodDependency(x)).flat(),
      };
    } else if (typeof v === 'string') {
      return {
        ...results,
        dependencies: parsePodDependency(v),
      };
    }

    return results;
  });
};

export function parsePodfileLock(fileContent: string): PodfileLock | null {
  const contents =
    loadPodfileLock(fileContent) ??
    loadPodfileLock(EXTERNAL_SOURCES_KEY + fileContent.split(EXTERNAL_SOURCES_KEY).slice(1));
  if (!contents) {
    return null;
  }

  const parsed = Object.entries(contents).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [kebabCaseToCamelCase(rubyCaseToKebab(key))]: value,
    };
  }, {}) as any;

  if (Array.isArray(parsed.pods)) {
    const parsedPods = parsed.pods.map(parsePodDependency);
    parsed.pods = parsedPods.flat();
  }

  return parsed;
}

function splitPodNameVersion(pod: string): { name: string; version?: string } {
  const [name] = pod.split(' ');
  return { name, version: pod.match(/\((.*)\)/)?.[1] };
}

function rubyCaseToKebab(str: string) {
  return str.toLowerCase().split(' ').join('-');
}

function kebabCaseToCamelCase(str: string) {
  return str.replace(/-([a-z])/g, g => g[1].toUpperCase());
}

export function getFilePathForExternalSource(podLock: PodfileLock, pod: string): string | null {
  const source = podLock.externalSources?.[pod];
  return source?.[':podspec'] ?? source?.[':path'] ?? null;
}
